import { createReducer } from '@reduxjs/toolkit';
import type { OrganizationsState, Organization } from '../types/organizations';
import type { components } from '../../magpiie-client/api-types';
import { logout } from '../types';
import {
  fetchOrganizations,
  createOrganization,
  switchOrganization,
  resetOrganizationsFetched,
} from '../actions/organizations';
import { fetchOrganizationAbilities } from '../actions/abilities';

type UserOrganization = components['schemas']['GetUserOrganizationDto'];

// Initialize active organization from localStorage
const getInitialActiveOrgId = (): number | null => {
  const stored = localStorage.getItem('activeOrganizationId');
  return stored ? parseInt(stored, 10) : null;
};

// Pick the default org to show on login when nothing valid is stored.
// Prefer an org the user owns (OWNER + ACCEPTED), then any accepted org,
// then fall back to the first org in the list.
const pickDefaultOrg = (
  orgsArray: UserOrganization[]
): UserOrganization | undefined => {
  const withOrg = orgsArray.filter((o) => o?.organization);
  return (
    withOrg.find(
      (o) => o.role === 'OWNER' && o.invitation_status === 'ACCEPTED'
    ) ??
    withOrg.find((o) => o.invitation_status === 'ACCEPTED') ??
    withOrg[0]
  );
};

const initialState: OrganizationsState = {
  isOrganizationLoading: false,
  hasFetchedOrganizations: false,
  organizations: {},
  activeOrganizationId: getInitialActiveOrgId(),
  abilityRulesByOrg: {},
  abilityRulesFetchedByOrg: {},
};

export const organizationsReducer = createReducer(initialState, (builder) => {
  builder
    // Fetch organizations - pending
    .addCase(fetchOrganizations.pending, (state) => {
      state.isOrganizationLoading = true;
    })
    // Fetch organizations - fulfilled
    .addCase(fetchOrganizations.fulfilled, (state, action) => {
      state.isOrganizationLoading = false;
      state.hasFetchedOrganizations = true;

      const orgsArray = Array.isArray(action.payload) ? action.payload : [];

      // Convert array to Record keyed by organization_id
      // Flatten the nested structure from GetUserOrganizationDto
      const orgsRecord: Record<string, Organization> = {};
      orgsArray.forEach((userOrg: any) => {
        if (userOrg.organization) {
          const flatOrg: Organization = {
            organization_id: userOrg.organization.id,
            name: userOrg.organization.name,
            slug: userOrg.organization.slug,
            description: userOrg.organization.description,
            image: userOrg.organization.image,
            billing_email: userOrg.organization.billing_email,
            creator_id: userOrg.organization.creator_id,
            created_at: userOrg.organization.created_at,
            role: userOrg.role,
            invitation_status: userOrg.invitation_status,
            invitation_sent_at: userOrg.invitation_sent_at,
          };
          orgsRecord[flatOrg.organization_id.toString()] = flatOrg;
        }
      });
      state.organizations = orgsRecord;

      // Auto-select default organization (prefer owned) if none is active
      if (!state.activeOrganizationId && orgsArray.length > 0) {
        const defaultOrg = pickDefaultOrg(orgsArray as UserOrganization[]);
        if (defaultOrg?.organization) {
          state.activeOrganizationId = defaultOrg.organization.id;
          localStorage.setItem(
            'activeOrganizationId',
            defaultOrg.organization.id.toString()
          );
        }
      }

      // Validate that stored activeOrganizationId still exists
      if (
        state.activeOrganizationId &&
        !orgsRecord[state.activeOrganizationId.toString()]
      ) {
        // Stored org no longer exists, select default (prefer owned)
        const defaultOrg = pickDefaultOrg(orgsArray as UserOrganization[]);
        if (defaultOrg?.organization) {
          state.activeOrganizationId = defaultOrg.organization.id;
          localStorage.setItem(
            'activeOrganizationId',
            defaultOrg.organization.id.toString()
          );
        } else {
          state.activeOrganizationId = null;
          localStorage.removeItem('activeOrganizationId');
        }
      }
    })
    // Fetch organizations - rejected
    .addCase(fetchOrganizations.rejected, (state) => {
      state.isOrganizationLoading = false;
      state.hasFetchedOrganizations = true; // Still true to prevent re-fetching on error
    })
    // Create organization - pending
    .addCase(createOrganization.pending, (state) => {
      state.isOrganizationLoading = true;
    })
    // Create organization - fulfilled
    .addCase(createOrganization.fulfilled, (state, action) => {
      state.isOrganizationLoading = false;

      if (action.payload) {
        const newOrg: Organization = {
          organization_id: action.payload.id,
          name: action.payload.name,
          slug: action.payload.slug,
          description: action.payload.description,
          image: action.payload.image,
          billing_email: action.payload.billing_email,
          creator_id: action.payload.creator_id,
          created_at: action.payload.created_at,
          role: 'OWNER' as any, // Creator is always owner
          invitation_status: 'ACCEPTED' as any,
          invitation_sent_at: null,
        };

        // Add to organizations
        state.organizations[newOrg.organization_id.toString()] = newOrg;

        // Set as active organization
        state.activeOrganizationId = newOrg.organization_id;
        localStorage.setItem(
          'activeOrganizationId',
          newOrg.organization_id.toString()
        );
      }
    })
    // Create organization - rejected
    .addCase(createOrganization.rejected, (state) => {
      state.isOrganizationLoading = false;
    })
    // Switch organization
    .addCase(switchOrganization, (state, action) => {
      const orgId = action.payload;
      // Only switch if organization exists
      if (state.organizations[orgId.toString()]) {
        state.activeOrganizationId = orgId;
        localStorage.setItem('activeOrganizationId', orgId.toString());
      }
    })
    // Reset fetched flag to allow re-fetching (e.g., after accepting invitation)
    .addCase(resetOrganizationsFetched, (state) => {
      state.hasFetchedOrganizations = false;
    })
    // Store CASL ability rules per organization
    .addCase(fetchOrganizationAbilities.fulfilled, (state, action) => {
      const { orgId, rules } = action.payload;
      state.abilityRulesByOrg[orgId] = rules;
      state.abilityRulesFetchedByOrg[orgId] = true;
    })
    // Still mark as fetched on error to prevent infinite refetching
    // (e.g. PENDING/REJECTED membership or stale activeOrganizationId).
    .addCase(fetchOrganizationAbilities.rejected, (state, action) => {
      const orgId = action.meta.arg;
      state.abilityRulesFetchedByOrg[orgId] = true;
    })
    // Logout - clear organization state
    .addCase(logout, (state) => {
      state.organizations = {};
      state.activeOrganizationId = null;
      state.hasFetchedOrganizations = false;
      state.isOrganizationLoading = false;
      state.abilityRulesByOrg = {};
      state.abilityRulesFetchedByOrg = {};
      localStorage.removeItem('activeOrganizationId');
    });
});
